/**
 * Deleting everything. FR-607, L-06, and the crypto-shredding half of T-12.
 *
 * ## Why this is one function and not three calls in a component
 *
 * A wipe has an order and a completeness requirement, and both are easy to get
 * subtly wrong from a button handler. The first version of the delete button
 * cleared entries and destroyed the keyring but left two things in
 * localStorage — and one of them, the eviction breadcrumb, then made the app
 * tell the user *"your browser may have cleared your data"* immediately after
 * they had deliberately deleted it themselves. Alarming, and precisely backwards.
 *
 * So the sequence lives here, with a test, and callers get one call.
 *
 * ## What this can and cannot promise
 *
 * Everything on this device goes. Ciphertext already replicated to a relay
 * (from v0.5) is beyond reach — destroying the key makes it permanently
 * undecryptable, which is the only guarantee this architecture can honestly
 * make. The UI says exactly that; see `data.delete.honest`.
 */
import { deleteAllEntries } from './entryStore'
import { destroyKeyring } from './keyring'
import { clearStorageEvidence } from './storageHealth'
import { clearDeviceId } from './deviceId'
import { withdrawSyncConsent } from './syncConsent'
import { clearSyncIdentity } from './syncIdentityStore'
import { openDb, STORE, txDone } from './db'

export async function wipeEverything(): Promise<void> {
  // 1. The entries themselves.
  await deleteAllEntries()

  // 2. Settings, symptom definitions and custom fields. A user deleting their
  //    health history has not asked to keep a list of the symptoms they track.
  const db = await openDb()
  const tx = db.transaction([STORE.settings, STORE.symptoms, STORE.customFields], 'readwrite')
  tx.objectStore(STORE.settings).clear()
  tx.objectStore(STORE.symptoms).clear()
  tx.objectStore(STORE.customFields).clear()
  await txDone(tx)

  // 3. The breadcrumb that says entries once existed. Left behind, it makes the
  //    next launch claim the browser evicted data the user deleted on purpose.
  clearStorageEvidence()

  // 4. The device identifier. It is not an account and not a fingerprint, but it
  //    is a persistent id the user just asked to be rid of.
  clearDeviceId()

  // 5. Sync consent. A record surviving the wipe would leave the app believing
  //    it still had permission to send data it no longer has — and consent to
  //    processing a history that has been deleted is meaningless anyway.
  await withdrawSyncConsent()

  // 6. The sync identity. Sealed under the DEK, so destroying the keyring below
  //    already makes it unreadable — but it is removed explicitly anyway. An
  //    identity that survives a wipe is a device that can rejoin the account, and
  //    "delete everything" must not leave a way back in.
  await clearSyncIdentity()

  // 7. The key, last. Once this is gone nothing above could be read anyway, but
  //    doing it last means an interrupted wipe leaves data readable rather than
  //    leaving readable-looking records nobody can open.
  await destroyKeyring()
}
